import { motion, AnimatePresence } from 'framer-motion';
import { AdminData } from './AdminLoader';
import {
  TableContainer,
  TableHeader,
  TablePagination,
  ErrorState,
  GlobalAdminTable,
  SplitEntityTables,
} from './components';

export interface AdminTableProps {
  adminTitle: string;
  filterDetail: string;
  hasActiveFilters: boolean;
  clearFilters: () => void;
  searchQuery: string;
  setSearchQuery: (query: string) => void;
  setCurrentPage: (page: number) => void;
  isLoading: boolean;
  admins: AdminData[];
  handleModifyRole?: (registrantId: string, newRole: string) => void;
  totalPages: number;
  currentPage: number;
  className?: string;
  error?: string | null;
}

export function getAdminName(admin: AdminData): string {
  if ('name' in admin && admin.name) {
    return admin.name;
  }

  if ('fname' in admin || 'lname' in admin) {
    return `${admin.fname || 'Unknown'} ${admin.lname || 'Admin'}`;
  }

  return 'Unknown Admin';
}

const getAdminRole = (admin: AdminData): string => {
  if ('isglobal' in admin) {
    return admin.isglobal ? 'Global Admin' : 'Admin';
  }
  return 'Member';
};

const isGlobalAdmin = (admin: AdminData): boolean => {
  return 'isglobal' in admin && admin.isglobal;
};

export function AdminTable({
  adminTitle,
  filterDetail,
  hasActiveFilters,
  clearFilters,
  searchQuery,
  setSearchQuery,
  setCurrentPage,
  isLoading,
  admins,
  handleModifyRole,
  totalPages,
  currentPage,
  className = 'w-3/4',
  error,
}: AdminTableProps) {
  return (
    <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ duration: 0.2 }}>
      <TableContainer className={className}>
        <TableHeader
          title={adminTitle}
          subtitle={filterDetail}
          hasActiveFilters={hasActiveFilters}
          clearFilters={clearFilters}
          searchQuery={searchQuery}
          setSearchQuery={setSearchQuery}
          setCurrentPage={setCurrentPage}
        />

        <AnimatePresence mode="wait">
          {error ? (
            <motion.div
              key="error"
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              transition={{ duration: 0.2 }}
            >
              <ErrorState error={error} clearFilters={clearFilters} />
            </motion.div>
          ) : (
            <motion.div
              key="content"
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              transition={{ duration: 0.2 }}
            >
              <AdminContentRenderer
                isLoading={isLoading}
                admins={admins}
                handleModifyRole={handleModifyRole}
                showModifyOption={!!filterDetail}
              />
            </motion.div>
          )}
        </AnimatePresence>

        {!error && (
          <TablePagination
            totalPages={totalPages}
            currentPage={currentPage}
            onPageChange={setCurrentPage}
          />
        )}
      </TableContainer>
    </motion.div>
  );
}

interface AdminContentRendererProps {
  isLoading: boolean;
  admins: AdminData[];
  handleModifyRole?: (registrantId: string, newRole: string) => void;
  showModifyOption: boolean;
}

function AdminContentRenderer({
  isLoading,
  admins,
  handleModifyRole,
  showModifyOption,
}: AdminContentRendererProps) {
  const globalAdmins = admins.filter(admin => isGlobalAdmin(admin));
  const regularAdmins = admins.filter(admin => getAdminRole(admin) === 'Admin');
  const members = admins.filter(admin => getAdminRole(admin) === 'Member');

  return (
    <AnimatePresence mode="wait">
      {isLoading ? (
        <motion.div
          key="loading"
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          transition={{ duration: 0.2 }}
          className="flex h-[400px] items-center justify-center"
        >
          <div className="text-[var(--anl-text-muted)]">Loading…</div>
        </motion.div>
      ) : !showModifyOption && globalAdmins.length > 0 ? (
        <motion.div
          key="global-admins"
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          transition={{ duration: 0.2 }}
        >
          <GlobalAdminTable isLoading={false} admins={globalAdmins} getUserName={getAdminName} />
        </motion.div>
      ) : showModifyOption ? (
        <motion.div
          key="split-tables"
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          transition={{ duration: 0.2 }}
        >
          <SplitEntityTables
            isLoading={false}
            admins={regularAdmins}
            members={members}
            getUserName={getAdminName}
            handleModifyRole={handleModifyRole}
          />
        </motion.div>
      ) : (
        <motion.div
          key="all-admins"
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          transition={{ duration: 0.2 }}
        >
          <GlobalAdminTable isLoading={false} admins={admins} getUserName={getAdminName} />
        </motion.div>
      )}
    </AnimatePresence>
  );
}
